import isEmpty from 'lodash/isEmpty';
import React, { useMemo } from 'react';
import List from '../../../components/list/List';
import text, { formatSqmArea, formatCurrency } from '../../../text';
import classNames from 'classnames';
import { Charge } from '../../services/coverage/workers/quote.worker';
import { isMembershipPriceId } from '../../../utilities/membership';
import { useSelector } from 'react-redux';
import { userHasActiveMembership } from '../../../utilities/user';
import useUser from '../../hooks/useUser';
import { usePaymentContext } from '../../pages/payment';

const getArea = (details?: Charge['details']): string | null =>
  details?.area_in_sqm ? formatSqmArea(details.area_in_sqm.toFixed()) : null;

const getDescription = (
  displayName: string,
  details?: Charge['details']
): string =>
  details?.area_in_sqm
    ? text('productData', {
        product: displayName,
        area: getArea(details),
      })
    : displayName;

const ChargeItem = ({
  charge,
  isApproximate = false,
  areaIsAlignedMiddle = false,
  hideArea = false,
}: {
  charge: Charge;
  isApproximate?: boolean;
  areaIsAlignedMiddle?: boolean;
  hideArea?: boolean;
}) => {
  const { job } = usePaymentContext();
  const currentUser = useUser();

  const { membershipPriceId } = useSelector((state: any) => ({
    membershipPriceId: state.order.membershipPriceId,
  }));

  const displayName = useMemo(
    () => text(`${charge.details?.category_name}3D`),
    [charge.details?.category_name]
  );

  const discounts = useMemo(
    () =>
      isMembershipPriceId(membershipPriceId)
        ? charge.member_discounts_preview
        : charge.discounts,
    [charge.member_discounts_preview, charge.discounts, membershipPriceId]
  );

  const isIndividualDiscountVisible = useMemo(() => {
    return (
      !userHasActiveMembership(currentUser) &&
      !isMembershipPriceId(membershipPriceId)
    );
  }, [currentUser, membershipPriceId]);

  return (
    <tbody className='ChargeItem'>
      {/* @ts-expect-error midText */}
      <List.Item
        className={classNames('charge', {
          isApproximate: isApproximate && charge.price?.base,
        })}
        description={
          hideArea || areaIsAlignedMiddle
            ? displayName
            : getDescription(displayName, charge.details)
        }
        value={
          charge.price?.base
            ? formatCurrency(
                charge.price.base,
                job?.quote?.currency.name,
                job?.quote?.currency.scale_factor,
                {},
                true
              )
            : text('toBeConfirmed')
        }
        {...(areaIsAlignedMiddle && {
          midText: `Area: ${getArea(charge.details)}`,
        })}
      />

      {charge.fees?.map((fee, i) => (
        <List.Item
          key={i}
          description={fee.display_name}
          value={`${formatCurrency(
            fee.price.total,
            job?.quote?.currency.name,
            job?.quote?.currency.scale_factor,
            {},
            true
          )}`}
          // @ts-expect-error midText
          midText={areaIsAlignedMiddle ? <span /> : null}
          // add span to align tr > td if there's a middle text
        />
      ))}

      {!isEmpty(discounts) &&
        discounts.map((discount, i) => (
          <List.Item
            key={i}
            description={discount.display_name}
            value={`-${formatCurrency(
              discount.price.discount,
              job?.quote?.currency.name,
              job?.quote?.currency.scale_factor,
              {},
              true
            )}`}
            className='discount font-pink pl-3 font-italic'
            // @ts-expect-error midText
            midText={areaIsAlignedMiddle ? <span /> : null}
            // add span to align tr > td if there's a middle text
          />
        ))}
    </tbody>
  );
};

export default ChargeItem;
